import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface DashboardData {
  system_stats: {
    total_jobs: number;
    completed_jobs: number;
    failed_jobs: number;
    processing_jobs: number;
    queued_jobs: number;
    cancelled_jobs: number;
    avg_processing_time: number;
    active_users: number;
    first_job_date: string;
  };
  popular_workflows: Array<{
    workflow_name: string;
    workflow_id: number;
    job_count: number;
    completed_count: number;
    failed_count: number;
    avg_duration: number;
    last_used: string;
  }>;
  active_users: Array<{
    username: string;
    user_id: number;
    job_count: number;
    completed_count: number;
    last_activity: string;
  }>;
  usage_by_hour: Array<{
    hour: number;
    job_count: number;
  }>;
  view_type: 'system' | 'personal';
}

const DashboardPage: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30days');
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_BASE}/analytics/dashboard`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const calculateSuccessRate = (completed: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-600">Loading dashboard...</div>;
  }

  if (!data) {
    return <div className="text-center text-red-600 py-12">Failed to load dashboard data</div>;
  }

  const { system_stats, popular_workflows, active_users, usage_by_hour, view_type } = data;
  const successRate = calculateSuccessRate(system_stats.completed_jobs, system_stats.total_jobs);
  const isPersonalView = view_type === 'personal';

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">
            {isPersonalView ? 'My Analytics' : 'System Analytics'}
          </h2>
          {isPersonalView && (
            <p className="text-gray-600 mt-1">Your personal generation statistics and activity</p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          {user?.role === 'super_admin' && (
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
              System Admin View
            </div>
          )}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl">
          <div className="text-3xl font-bold">{system_stats.total_jobs}</div>
          <div className="text-sm opacity-90">{isPersonalView ? 'My Jobs' : 'Total Jobs'}</div>
          <div className="text-xs opacity-75 mt-1">
            Since {new Date(system_stats.first_job_date).toLocaleDateString()}
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl">
          <div className="text-3xl font-bold">{successRate}%</div>
          <div className="text-sm opacity-90">Success Rate</div>
          <div className="text-xs opacity-75 mt-1">
            {system_stats.completed_jobs} completed
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl">
          <div className="text-3xl font-bold">{formatDuration(system_stats.avg_processing_time)}</div>
          <div className="text-sm opacity-90">Avg Processing Time</div>
          <div className="text-xs opacity-75 mt-1">
            Per successful job
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-xl">
          <div className="text-3xl font-bold">{system_stats.active_users}</div>
          <div className="text-sm opacity-90">{isPersonalView ? 'You' : 'Active Users'}</div>
          <div className="text-xs opacity-75 mt-1">
            {isPersonalView ? 'Have submitted jobs' : 'Have submitted jobs'}
          </div>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            {isPersonalView ? 'My Job Status' : 'Job Status Breakdown'}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-gray-700">Completed</span>
              </div>
              <div className="text-right">
                <div className="font-semibold">{system_stats.completed_jobs}</div>
                <div className="text-sm text-gray-500">
                  {calculateSuccessRate(system_stats.completed_jobs, system_stats.total_jobs)}%
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-gray-700">Failed</span>
              </div>
              <div className="text-right">
                <div className="font-semibold">{system_stats.failed_jobs}</div>
                <div className="text-sm text-gray-500">
                  {calculateSuccessRate(system_stats.failed_jobs, system_stats.total_jobs)}%
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span className="text-gray-700">Processing</span>
              </div>
              <div className="text-right">
                <div className="font-semibold">{system_stats.processing_jobs}</div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span className="text-gray-700">Queued</span>
              </div>
              <div className="text-right">
                <div className="font-semibold">{system_stats.queued_jobs}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            {isPersonalView ? 'My Usage by Hour' : 'Usage by Hour'}
          </h3>
          <div className="space-y-2">
            {usage_by_hour.length > 0 ? (
              usage_by_hour.map((hour) => (
                <div key={hour.hour} className="flex items-center space-x-3">
                  <div className="w-16 text-sm text-gray-600">
                    {hour.hour.toString().padStart(2, '0')}:00
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min((hour.job_count / Math.max(...usage_by_hour.map(h => h.job_count))) * 100, 100)}%`
                      }}
                    ></div>
                  </div>
                  <div className="w-8 text-sm text-gray-700 text-right">
                    {hour.job_count}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">No activity in the last 7 days</div>
            )}
          </div>
        </div>
      </div>

      {/* Workflows and Users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            {isPersonalView ? 'My Favorite Workflows' : 'Most Popular Workflows'}
          </h3>
          <div className="space-y-4">
            {popular_workflows.length > 0 ? (
              popular_workflows.slice(0, 5).map((workflow, index) => (
                <div key={workflow.workflow_id} className="flex items-center space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {workflow.workflow_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {workflow.job_count} jobs • {calculateSuccessRate(workflow.completed_count, workflow.job_count)}% success
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {formatDuration(workflow.avg_duration)}
                    </div>
                    <div className="text-xs text-gray-500">avg time</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">No workflows used yet</div>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            {isPersonalView ? 'My Activity' : 'Most Active Users'}
          </h3>
          <div className="space-y-4">
            {active_users.length > 0 ? (
              active_users.slice(0, 5).map((user_data, index) => (
                <div key={user_data.user_id} className="flex items-center space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-semibold">
                    {isPersonalView ? '👤' : index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {isPersonalView ? 'You' : user_data.username}
                    </div>
                    <div className="text-sm text-gray-500">
                      {user_data.job_count} jobs • {calculateSuccessRate(user_data.completed_count, user_data.job_count)}% success
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">
                      Last: {new Date(user_data.last_activity).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">No activity yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
